import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { X, Loader2, CalendarClock, Search, Check, MapPin } from 'lucide-react';
import { toast } from 'sonner';
import ConstructionBreadcrumb from './ConstructionBreadcrumb';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STATUS_BADGE = {
  NOT_STARTED: 'bg-slate-100 text-slate-600', IN_PROGRESS: 'bg-blue-100 text-blue-700',
  COMPLETED: 'bg-emerald-100 text-emerald-700', ON_HOLD: 'bg-amber-100 text-amber-700', UNKNOWN: 'bg-slate-100 text-slate-500',
};

// Links a piece of field evidence (analysis job, measurement, etc.) to a schedule Activity.
// Uses the ACTIVE schedule and the current ConstructionContext to suggest relevant activities,
// then server-side search for the rest. The engineer always confirms — no auto-linking.
const LinkActivityModal = ({ projectId, evidenceType = 'job', evidenceId, constructionContext, onClose, onLinked }) => {
  const [schedule, setSchedule] = useState(undefined); // undefined=loading, null=none
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [useContext, setUseContext] = useState(true);
  const [loading, setLoading] = useState(true);
  const [linkingId, setLinkingId] = useState(null);

  const ctx = constructionContext || {};
  const ctxParams = useContext
    ? { buildingId: ctx.buildingId, floorId: ctx.floorId, zoneId: ctx.zoneId, tradeId: ctx.tradeId }
    : {};

  useEffect(() => {
    axios.get(`${API}/projects/${projectId}/schedules`, { params: { status: 'ACTIVE' } })
      .then((r) => setSchedule(r.data[0] || null))
      .catch(() => setSchedule(null));
  }, [projectId]);

  const loadActivities = useCallback(async () => {
    if (!schedule) { setLoading(false); return; }
    setLoading(true);
    try {
      const params = { page: 1, pageSize: 25, search: search || undefined };
      Object.entries(ctxParams).forEach(([k, v]) => { if (v) params[k] = v; });
      const d = await axios.get(`${API}/projects/${projectId}/schedules/${schedule.id}/activities`, { params }).then((r) => r.data);
      setItems(d.items || []); setTotal(d.total || 0);
    } catch (e) { toast.error('Failed to load activities'); } finally { setLoading(false); }
  }, [projectId, schedule, search, useContext]); // eslint-disable-line

  useEffect(() => { if (schedule !== undefined) loadActivities(); }, [schedule, loadActivities]);

  const link = async (activityId) => {
    setLinkingId(activityId);
    try {
      const clientKey = (window.crypto?.randomUUID ? window.crypto.randomUUID() : `${evidenceId || 'ev'}-${activityId}-${Date.now()}`);
      await axios.post(`${API}/projects/${projectId}/activities/${activityId}/evidence`, { evidenceType, evidenceId, clientKey });
      toast.success('Evidence linked to activity');
      onLinked && onLinked(activityId); onClose();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to link'); } finally { setLinkingId(null); }
  };

  const hasCtx = Object.values(ctxParams).some(Boolean);

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4" data-testid="link-activity-modal">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[85vh] flex flex-col">
        <div className="flex items-center justify-between p-5 border-b border-slate-100">
          <h2 className="font-bold text-slate-900 flex items-center gap-2"><CalendarClock className="w-5 h-5 text-blue-600" /> Link to Schedule Activity</h2>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700" data-testid="link-activity-close"><X className="w-5 h-5" /></button>
        </div>

        {schedule === undefined ? (
          <div className="flex items-center justify-center py-16 text-slate-500"><Loader2 className="w-6 h-6 animate-spin mr-2" /> Loading…</div>
        ) : schedule === null ? (
          <div className="p-6 text-sm text-slate-500" data-testid="link-activity-noschedule">No ACTIVE schedule for this project yet. Ask an admin to import and activate a schedule.</div>
        ) : (
          <>
            <div className="p-4 space-y-3 border-b border-slate-100">
              <p className="text-xs text-slate-500">Active schedule: <b className="text-slate-700">{schedule.name}</b> · {schedule.revision}</p>
              <div className="relative">
                <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search activity name / code" className="w-full pl-9 pr-3 py-2 border border-slate-300 rounded-lg text-sm" data-testid="link-activity-search" />
              </div>
              {hasCtx && (
                <label className="flex items-center gap-2 text-xs text-slate-600 cursor-pointer" data-testid="link-activity-ctx-toggle-label">
                  <input type="checkbox" checked={useContext} onChange={(e) => setUseContext(e.target.checked)} className="w-4 h-4 accent-blue-600" data-testid="link-activity-ctx-toggle" />
                  <MapPin className="w-3.5 h-3.5 text-blue-500" /> Suggest by current location/trade
                </label>
              )}
            </div>
            <div className="flex-1 overflow-auto p-3">
              {loading ? (
                <div className="flex items-center justify-center py-10 text-slate-400"><Loader2 className="w-5 h-5 animate-spin" /></div>
              ) : items.length === 0 ? (
                <p className="text-sm text-slate-400 text-center py-10" data-testid="link-activity-empty">No matching activities.{useContext && hasCtx ? ' Turn off the location suggestion to search all.' : ''}</p>
              ) : (
                <div className="space-y-2">
                  {items.map((a) => (
                    <div key={a.id} className="border border-slate-200 rounded-lg p-3 flex items-center justify-between gap-2" data-testid="link-activity-row">
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-slate-800 truncate">{a.activityCode ? `${a.activityCode} · ` : ''}{a.activityName}</p>
                        <div className="flex items-center gap-2 mt-1 flex-wrap">
                          <span className={`text-[10px] px-2 py-0.5 rounded-full ${STATUS_BADGE[a.status] || STATUS_BADGE.UNKNOWN}`}>{a.status}</span>
                          <ConstructionBreadcrumb projectId={projectId} context={a.constructionContext} fallback="Unmapped" testId={`link-act-ctx-${a.id}`} />
                        </div>
                      </div>
                      <button onClick={() => link(a.id)} disabled={linkingId === a.id} className="px-3 py-1.5 bg-blue-600 text-white text-xs rounded-lg flex items-center gap-1 disabled:opacity-50 shrink-0" data-testid="link-activity-confirm">
                        {linkingId === a.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />} Link
                      </button>
                    </div>
                  ))}
                  <p className="text-[11px] text-slate-400 text-center pt-1">{total} matching · showing first {items.length}</p>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default LinkActivityModal;
